import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, ChevronDown } from 'lucide-react';
import api from '../lib/api';

/**
 * Levels — explains the points system transparently:
 *   • Level ladder (Explorer → City Icon)
 *   • Earn-table with every action and its points
 *   • Anti-spam note (vibes/stories only give small points)
 */
export default function Levels() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [cat, mine, hist] = await Promise.all([
          api.get('/reputation/catalog'),
          api.get('/reputation/me'),
          api.get('/reputation/me/history?limit=15'),
        ]);
        if (alive) {
          setData({ ...cat.data, mine: mine.data });
          setHistory(hist.data.items || []);
        }
      } catch (e) { console.error(e); }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  if (loading || !data) {
    return <div className="min-h-[100dvh] bg-[#F7F6F2] flex items-center justify-center"><Loader2 size={20} className="text-[#9B9EA3] animate-spin" /></div>;
  }

  const lv = data.mine?.level;
  return (
    <div className="min-h-[100dvh] bg-[#F7F6F2]" data-testid="levels-page">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-black/[0.04]">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} data-testid="lv-back" className="w-9 h-9 rounded-full bg-[#F1F0EA] flex items-center justify-center">
            <ArrowLeft size={16} />
          </button>
          <h1 className="text-base font-extrabold text-[#111]">How Points Work</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5 space-y-5">
        {lv && (
          <section data-testid="lv-current" className="bg-white rounded-3xl border border-black/[0.04] p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="text-4xl">{lv.emoji}</div>
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#6B6E73]">Level {lv.level}</p>
                <p className="text-xl font-extrabold text-[#111]">{lv.name}</p>
              </div>
              <p className="text-2xl font-extrabold text-[#111] tabular-nums">{data.mine.points}</p>
            </div>
            {lv.next ? (
              <>
                <div className="w-full h-2.5 bg-[#F1F0EA] rounded-full overflow-hidden">
                  <div data-testid="lv-progress" className="h-full bg-[#B6E600] rounded-full transition-all" style={{ width: `${lv.pct_to_next}%` }} />
                </div>
                <p className="text-xs text-[#6B6E73] mt-2 font-medium">
                  <strong>{lv.points_to_next}</strong> pts to unlock <strong>{lv.next.name}</strong> {lv.next.emoji}
                </p>
              </>
            ) : (
              <p className="text-xs text-[#3F5200] font-extrabold">👑 You've maxed out the ladder!</p>
            )}
          </section>
        )}

        {/* Level ladder — now shows reward per level */}
        <section data-testid="lv-ladder">
          <h2 className="text-sm font-extrabold text-[#111] mb-2">The 8 Levels</h2>
          <div className="bg-white rounded-3xl border border-black/[0.04] overflow-hidden">
            {data.levels.map((L, i) => {
              const reached = (data.mine?.points ?? 0) >= L.min;
              return (
                <div key={L.level} className={`flex items-start gap-3 p-3 ${i > 0 ? 'border-t border-black/[0.04]' : ''} ${reached ? '' : 'opacity-55'}`}>
                  <div className="text-2xl flex-shrink-0">{L.emoji}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-extrabold text-[#111]">{L.name}</p>
                      <p className="text-[11px] text-[#6B6E73] tabular-nums">{L.min} – {L.max >= 100000 ? '∞' : L.max} pts</p>
                      {reached && <span className="text-[9px] uppercase tracking-wider font-extrabold text-[#3F5200] bg-[#B6E600]/20 px-2 py-0.5 rounded-full">Reached</span>}
                    </div>
                    {L.unlock && (
                      <p className="text-[11px] text-[#6B6E73] mt-1 leading-snug">
                        <span className="font-bold text-[#3F5200]">🎁 Unlocks:</span> {L.unlock}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Earn table */}
        <section data-testid="lv-earn">
          <h2 className="text-sm font-extrabold text-[#111] mb-2">Ways to earn points</h2>
          <div className="bg-white rounded-3xl border border-black/[0.04] overflow-hidden">
            {data.actions.map((a, i) => (
              <div key={a.key} className={`flex items-center gap-3 p-3 ${i > 0 ? 'border-t border-black/[0.04]' : ''}`}>
                <div className="text-lg">{a.icon}</div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-[#111]">{a.label}</p>
                  <p className="text-[10px] uppercase tracking-wider text-[#9B9EA3]">{a.category}</p>
                </div>
                <p className="text-sm font-extrabold text-[#B6E600] tabular-nums">+{a.points}</p>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-[#9B9EA3] mt-2 leading-relaxed">
            Real-world participation (joining, hosting, attending, reviews) is the fastest way up. Stories and vibes give small points to discourage spam.
          </p>
        </section>

        {/* Category leaderboards */}
        <section data-testid="lv-categories">
          <h2 className="text-sm font-extrabold text-[#111] mb-2">Category leaderboards</h2>
          <div className="bg-white rounded-3xl border border-black/[0.04] p-4">
            <p className="text-xs text-[#6B6E73] leading-relaxed mb-3">
              Every point you earn is tagged to a category. Whichever you participate in most determines where you rank.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {(data.categories || []).filter(c => c !== 'overall').map(c => (
                <span key={c} className="text-[11px] font-bold text-[#3F5200] bg-[#B6E600]/15 px-2.5 py-1 rounded-full capitalize">
                  {c}
                </span>
              ))}
            </div>
            <p className="text-[11px] text-[#9B9EA3] mt-3 leading-relaxed">
              Tap the rank chip on your profile to see how you compare in your city — switch categories from there.
            </p>
          </div>
        </section>

        {/* Anti-abuse rules */}
        <section data-testid="lv-anti-abuse">
          <h2 className="text-sm font-extrabold text-[#111] mb-2">Fair-play rules</h2>
          <div className="bg-white rounded-3xl border border-black/[0.04] divide-y divide-black/[0.04]">
            {[
              { icon: '⏱️', title: 'Daily caps per action', desc: 'Stories, vibes and comments are capped per day so high-volume posting cannot inflate scores.' },
              { icon: '🤝', title: 'Real engagement only', desc: 'Self-likes, sock-puppet follows, and reciprocal-only behavior are filtered out before they count.' },
              { icon: '🚫', title: 'No buying or trading', desc: 'Points cannot be purchased, transferred or gifted. Earn them by showing up.' },
              { icon: '⚖️', title: 'Reports & moderation', desc: 'Reported or removed content has its points reversed automatically.' },
            ].map((r, i) => (
              <div key={i} className="flex items-start gap-3 p-3">
                <div className="text-lg flex-shrink-0">{r.icon}</div>
                <div>
                  <p className="text-sm font-bold text-[#111]">{r.title}</p>
                  <p className="text-[11px] text-[#6B6E73] mt-0.5 leading-snug">{r.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section data-testid="lv-faq">
          <h2 className="text-sm font-extrabold text-[#111] mb-2">FAQ</h2>
          <div className="bg-white rounded-3xl border border-black/[0.04] divide-y divide-black/[0.04]">
            {[
              { q: 'Do I lose points over time?', a: 'No — points never expire. Streaks may reset if you stay inactive, but your level stays.' },
              { q: 'Can I lose points?', a: 'Yes, if content you posted is removed for breaking guidelines, those points are reversed.' },
              { q: 'How long until I see new points?', a: 'Instantly. Tap your level on this page to refresh the recent points list.' },
              { q: 'Why is my city rank shown as a percentage?', a: 'When your city has a small population, percentile bands (Top 10%, Top 25%) are friendlier than raw counts.' },
              { q: 'Can perks be revoked?', a: 'Only if your account is moderated. Otherwise once unlocked, always yours.' },
            ].map((f, i) => (
              <details key={i} className="group">
                <summary className="flex items-center justify-between gap-3 p-3 cursor-pointer list-none">
                  <p className="text-sm font-bold text-[#111] flex-1">{f.q}</p>
                  <ChevronDown size={16} className="text-[#9B9EA3] transition-transform group-open:rotate-180 flex-shrink-0" />
                </summary>
                <p className="text-[12px] text-[#6B6E73] px-3 pb-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Recent history */}
        {history.length > 0 && (
          <section data-testid="lv-history">
            <h2 className="text-sm font-extrabold text-[#111] mb-2">Your recent points</h2>
            <div className="bg-white rounded-3xl border border-black/[0.04] overflow-hidden">
              {history.map((h, i) => (
                <div key={h.id} className={`flex items-center gap-3 p-3 ${i > 0 ? 'border-t border-black/[0.04]' : ''}`}>
                  <div className="text-lg">{h.icon || '⭐'}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#111] truncate">{h.label}</p>
                    <p className="text-[10px] text-[#9B9EA3]">{h.created_at?.slice(0, 10)}</p>
                  </div>
                  <p className="text-sm font-extrabold text-[#B6E600] tabular-nums">+{h.points}</p>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
